<script>
    import Configure from './configure'
    import {compose, emphasize, emphasizeAgainst, ignoreWhenColorIs, setAlpha} from "../helpers/color-generator";

    export default {
        mixins: [
            Configure
        ],
        computed: {
            staticStyle() {
                return {
                    'success_color': 'rgb(0,211,138)',
                    'error_color': 'rgb(247,0,114)',
                    'info_color': 'rgb(14,108,190)',
                    'success_button_text_color': '#fff',
                    'success_button_background_color': '#00d38a',
                    'success_button_border_color': '#00d38a',
                    'youtube_button_text_color': '#fff',
                    'youtube_button_background_color': '#ff1414',
                    'youtube_button_border_color': '#ff1414',
                    'twitter_button_text_color': '#fff',
                    'twitter_button_background_color': '#00a4db',
                    'twitter_button_border_color': '#00a4db',
                }
            },
            style() {
                const get = (category, item) => {
                    if (category === 'static')
                        return this.staticStyle[item];
                    else
                        return this.$styleConfig[category][item].value;
                };
                const transform = (category, item, ...transformers) => compose(get(category, item), ...transformers);

                const buttonEmphasizeAmount = {
                    hover: 5,
                    active: 7,
                    disabled: 11,
                    strongHover: 11,
                    strongActive: 16,
                    strongDisabled: 21
                };

                const inputDisabledEmphasizeAmount = 2;

                return {
                    /** General **/
                    '--highlight-color': get('main', 'highlight_color'),
                    '--highlight-color-overlay': transform('main', 'highlight_color', setAlpha(.6)),
                    '--highlight-color-faded': transform('main', 'highlight_color', setAlpha(.125)),
                    '--success-color': transform('static', 'success_color', emphasizeAgainst(get('main', 'background'), 20)),
                    '--success-color-faded': transform('static', 'success_color', emphasizeAgainst(get('main', 'background'), 20), setAlpha(.1)),
                    '--error-color': transform('static', 'error_color', emphasizeAgainst(get('main', 'background'), 20)),
                    '--info-color': get('static', 'info_color'),
                    '--info-bg-color': get('static', 'info_color'),
                    '--info-text-color': transform('static', 'info_color', emphasizeAgainst(get('static', 'info_color'), 90)),
                    // '--info-border-color': get('static', 'info_border_color'),
                    '--error-color-faded': transform('static', 'error_color', emphasizeAgainst(get('main', 'background'), 20), setAlpha(.1)),
                    '--primary-text-color': get('main', 'primary_text'),
                    '--primary-text-color-faded': transform('main', 'primary_text', setAlpha(.3)),
                    '--primary-text-color-faded-more': transform('main', 'primary_text', setAlpha(.07)),
                    '--primary-text-color-emphasized': transform('main', 'primary_text', emphasize(40)),
                    '--primary-text-color-less-emphasized': transform('main', 'primary_text', emphasize(30)),
                    '--secondary-text-color': get('main', 'secondary_text'),
                    '--tertiary-text-color': get('main', 'tertiary_text'),
                    '--tertiary-text-color-reversed': transform('main', 'tertiary_text', emphasize(100)),
                    '--background': get('main', 'background'),
                    '--background-faded': transform('main', 'background', setAlpha(.8)),
                    '--background-modal': transform('main', 'background', setAlpha(.6)),
                    '--background-faded-little': transform('main', 'background', setAlpha(.9)),
                    '--background-emphasized': transform('main', 'background', emphasize(3)),
                    '--background-emphasized-more': transform('main', 'background', emphasize(6)),
                    '--border-color': transform('main', 'background', emphasize(15)),

                    /** Text/Input Fields **/
                    '--input-placeholder-color': get('main', 'input_placeholder_text'),
                    '--input-text-color': get('main', 'input_text'),
                    '--input-background-color': get('main', 'input_background'),
                    '--input-background-color-emphasized': transform('main', 'input_background', emphasize(3)),
                    '--input-border-color': get('main', 'input_border'),
                    // Active
                    '--input-active-background-color': get('main', 'input_background'),
                    '--input-active-border-color': get('main', 'highlight_color'),
                    // Disabled
                    '--input-disabled-placeholder-color': transform('main', 'input_placeholder_text', emphasize(inputDisabledEmphasizeAmount)),
                    '--input-disabled-text-color': transform('main', 'input_text', emphasize(inputDisabledEmphasizeAmount)),
                    '--input-disabled-background-color': transform('main', 'input_background', emphasize(inputDisabledEmphasizeAmount)),
                    '--input-disabled-border-color': transform('main', 'input_border', emphasize(inputDisabledEmphasizeAmount)),

                    /** Button | Buy, In Cart Button **/
                    // Default
                    '--button-text-color': get('main', 'button_text'),
                    '--button-background-color': get('main', 'button_background'),
                    '--button-border-color': get('main', 'button_background'),
                    // Outline
                    '--outline-button-text-color': get('main', 'button_background'),
                    '--outline-button-background-color': get('main', 'background'),
                    '--outline-button-border-color': get('main', 'button_background'),
                    // Success
                    '--success-button-text-color': get('static', 'success_button_text_color'),
                    '--success-button-background-color': get('static', 'success_button_background_color'),
                    '--success-button-border-color': get('static', 'success_button_border_color'),
                    // Neutral
                    '--neutral-button-text-color': transform('main', 'background', emphasizeAgainst(transform('main', 'background', emphasize(100)), 100)),
                    '--neutral-button-background-color': transform('main', 'background', emphasize(40)),
                    '--neutral-button-border-color': transform('main', 'background', emphasize(40)),
                    // YouTube
                    '--youtube-button-text-color': get('static', 'youtube_button_text_color'),
                    '--youtube-button-background-color': get('static', 'youtube_button_background_color'),
                    '--youtube-button-border-color': get('static', 'youtube_button_border_color'),
                    // Twitter
                    '--twitter-button-text-color': get('static', 'twitter_button_text_color'),
                    '--twitter-button-background-color': get('static', 'twitter_button_background_color'),
                    '--twitter-button-border-color': get('static', 'twitter_button_border_color'),

                    // Hover
                    '--button-hover-background-color': transform('main', 'button_background', emphasize(buttonEmphasizeAmount.hover)),
                    '--button-hover-border-color': transform('main', 'button_background', emphasize(buttonEmphasizeAmount.hover)),
                    // Hover - Success
                    '--success-button-hover-text-color': transform('static', 'success_button_text_color', emphasize(buttonEmphasizeAmount.hover)),
                    '--success-button-hover-background-color': transform('static', 'success_button_background_color', emphasize(buttonEmphasizeAmount.hover)),
                    '--success-button-hover-border-color': transform('static', 'success_button_border_color', emphasize(buttonEmphasizeAmount.hover)),
                    // Hover - Outline
                    '--outline-button-hover-background-color': transform('main', 'background', emphasize(buttonEmphasizeAmount.hover)),
                    '--outline-button-hover-border-color': get('main', 'button_background'),
                    // Hover - Neutral
                    '--neutral-button-hover-background-color': transform('main', 'background', emphasize(50)),
                    '--neutral-button-hover-border-color': transform('main', 'background', emphasize(50)),
                    // Hover - YouTube
                    '--youtube-button-hover-text-color': transform('static', 'youtube_button_text_color', emphasize(buttonEmphasizeAmount.strongHover)),
                    '--youtube-button-hover-background-color': transform('static', 'youtube_button_background_color', emphasize(buttonEmphasizeAmount.strongHover)),
                    '--youtube-button-hover-border-color': transform('static', 'youtube_button_border_color', emphasize(buttonEmphasizeAmount.strongHover)),
                    // Hover - Twitter
                    '--twitter-button-hover-text-color': transform('static', 'twitter_button_text_color', emphasize(buttonEmphasizeAmount.hover)),
                    '--twitter-button-hover-background-color': transform('static', 'twitter_button_background_color', emphasize(buttonEmphasizeAmount.hover)),
                    '--twitter-button-hover-border-color': transform('static', 'twitter_button_border_color', emphasize(buttonEmphasizeAmount.hover)),

                    // Active
                    '--button-active-background-color': transform('main', 'button_background', emphasize(buttonEmphasizeAmount.active)),
                    '--button-active-border-color': transform('main', 'button_background', emphasize(buttonEmphasizeAmount.active)),
                    // Active - Success
                    '--success-button-active-text-color': transform('static', 'success_button_text_color', emphasize(buttonEmphasizeAmount.active)),
                    '--success-button-active-background-color': transform('static', 'success_button_background_color', emphasize(buttonEmphasizeAmount.active)),
                    '--success-button-active-border-color': transform('static', 'success_button_border_color', emphasize(buttonEmphasizeAmount.active)),
                    // Active - Outline
                    '--outline-button-active-background-color': transform('main', 'background', emphasize(buttonEmphasizeAmount.active)),
                    '--outline-button-active-border-color': get('main', 'button_background'),
                    // Active - Neutral
                    '--neutral-button-active-background-color': transform('main', 'background', emphasize(60)),
                    '--neutral-button-active-border-color': transform('main', 'background', emphasize(60)),
                    // Active - YouTube
                    '--youtube-button-active-text-color': transform('static', 'youtube_button_text_color', emphasize(buttonEmphasizeAmount.strongActive)),
                    '--youtube-button-active-background-color': transform('static', 'youtube_button_background_color', emphasize(buttonEmphasizeAmount.strongActive)),
                    '--youtube-button-active-border-color': transform('static', 'youtube_button_border_color', emphasize(buttonEmphasizeAmount.strongActive)),
                    // Active - Twitter
                    '--twitter-button-active-text-color': transform('static', 'twitter_button_text_color', emphasize(buttonEmphasizeAmount.active)),
                    '--twitter-button-active-background-color': transform('static', 'twitter_button_background_color', emphasize(buttonEmphasizeAmount.active)),
                    '--twitter-button-active-border-color': transform('static', 'twitter_button_border_color', emphasize(buttonEmphasizeAmount.active)),

                    // Disabled
                    '--button-disabled-text-color': transform('main', 'button_text', emphasize(7)),
                    '--button-disabled-background-color': transform('main', 'button_background', emphasize(buttonEmphasizeAmount.disabled)),
                    '--button-disabled-border-color': transform('main', 'button_background', emphasize(buttonEmphasizeAmount.disabled)),
                    // Disabled - Success
                    '--success-button-disabled-text-color': get('static', 'success_button_text_color'),
                    '--success-button-disabled-background-color': get('static', 'success_button_background_color'),
                    '--success-button-disabled-border-color': get('static', 'success_button_border_color'),
                    // Disabled - Outline
                    '--outline-button-disabled-background-color': get('main', 'background'),
                    '--outline-button-disabled-border-color': get('main', 'button_background'),
                    // Disabled - Neutral
                    '--neutral-button-disabled-background-color': get('main', 'background'),
                    '--neutral-button-disabled-border-color': get('main', 'background'),
                    // Disabled - YouTube
                    '--youtube-button-disabled-text-color': get('static', 'youtube_button_text_color'),
                    '--youtube-button-disabled-background-color': get('static', 'youtube_button_background_color'),
                    '--youtube-button-disabled-border-color': get('static', 'youtube_button_border_color'),
                    // Disabled - Twitter
                    '--twitter-button-disabled-text-color': get('static', 'twitter_button_text_color'),
                    '--twitter-button-disabled-background-color': get('static', 'twitter_button_background_color'),
                    '--twitter-button-disabled-border-color': get('static', 'twitter_button_border_color'),

                    /** Play Button */
                    '--play-button-background-color': get('main', 'play_button_background'),
                    '--play-button-icon-color': get('main', 'play_button_icon'),

                    /** Tile **/
                    '--item-tile-button-text-color': get('main', 'item_tile_price'),
                    '--item-tile-button-background-color': get('main', 'item_tile_price_background'),
                    '--item-tile-border-color': get('main', 'item_tile_border'),
                    '--item-tile-background-color': get('main', 'item_tile_background'),
                    '--item-tile-primary-text-color': get('main', 'item_tile_primary_text'),
                    '--item-tile-secondary-text-color': get('main', 'item_tile_secondary_text'),

                    /** Item Row **/
                    '--item-row-hover-background-color': get('main', 'row_hover_background'),
                    '--item-row-active-background-color': get('main', 'row_active_background'),
                    '--item-row-hover-primary-text-color': get('main', 'row_hover_primary_text'),
                    '--item-row-active-primary-text-color': get('main', 'row_active_primary_text'),
                    '--item-row-hover-secondary-text-color': get('main', 'row_hover_secondary_text'),
                    '--item-row-active-secondary-text-color': get('main', 'row_active_secondary_text'),

                    /** Menu Panel **/
                    '--menu-panel-background-color': get('menu', 'background'),
                    '--menu-panel-border-color': transform('menu', 'background', emphasize(15)),
                    '--menu-item-text-color': get('menu', 'item_text'),
                    '--menu-item-text-color-faded': transform('menu', 'item_text', setAlpha(.3)),
                    '--menu-item-text-color-faded-more': transform('menu', 'item_text', setAlpha(.07)),
                    '--menu-item-active-text-color': get('menu', 'item_active_text'),
                    '--menu-item-active-text-color-faded': transform('menu', 'item_active_text', setAlpha(.125)),
                    '--menu-item-hover-text-color': get('menu', 'item_hover_text'),
                    '--menu-item-icon-color': get('menu', 'item_icon'),
                    '--menu-item-active-icon-color': get('menu', 'item_icon_active'),
                    '--menu-item-hover-icon-color': get('menu', 'item_icon_hover'),

                    /** Licenses Boxes */
                    '--license-button-background-color': get('main', 'license_discounts_button_background'),
                    '--license-button-text-color': transform('main', 'license_discounts_button_background',  emphasize(100)),
                    '--license-box-background-color': get('main', 'container_background'),
                    '--license-box-background-faded-color': transform('main', 'container_background', setAlpha(.5)),
                    '--license-box-text-color': get('main', 'container_text'),
                    '--license-highlight-1-color': get('main', 'highlighted_license')[0],
                    '--license-highlight-1-faded-color': compose(get('main', 'highlighted_license')[0], setAlpha(.5)),
                    '--license-highlight-2-color': get('main', 'highlighted_license')[1],
                    '--license-highlight-2-faded-color': compose(get('main', 'highlighted_license')[1], setAlpha(.5)),

                    '--license-highlight-contrast-color': compose(get('main', 'highlighted_license')[1], emphasize(100)),
                    /** Dialog Modal **/
                    '--dialog-modal-background': transform('main', 'background', ignoreWhenColorIs('white'), emphasize(7)),
                }
            }
        },
        methods: {}
    }
</script>
